import React, { useEffect, useState } from "react";
import {
  FileText,
  FileCheck2,
  Image as ImageIcon,
  Download,
  Trash2,
  Upload,
  Loader2,
  Lock,
  ExternalLink,
  ClipboardList,
  MessageCircle,
  Mail,
  BookOpen,
  Briefcase,
  Users,
  EyeOff,
  Save,
  Camera,
  type LucideIcon,
} from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import {
  StorageServiceError,
  type FileUpload,
  type StorageUserFile,
} from "@/services/storageService";
import { type Incident } from "./types";
import {
  getStageCompletion,
  showsExtendedStage,
} from "./StageTracker";
import { type GeneratedArtifactKind } from "./RestorationRoomChat";

// Same helpers exist in RestorationRoomChat — duplicated here to keep this
// component self-contained for Commit A. Will lift to a shared util if a
// third caller appears.
const formatFileSize = (bytes: number): string => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 KB";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const FileTypeIcon = ({ mimeType }: { mimeType: string }) => {
  if (mimeType?.startsWith("image/")) {
    return <ImageIcon className="w-4 h-4" aria-hidden />;
  }
  return <FileText className="w-4 h-4" aria-hidden />;
};

const formatRelativeTime = (iso: string): string => {
  const then = new Date(iso).getTime();
  if (!Number.isFinite(then)) return "";
  const diff = Date.now() - then;
  const minutes = Math.round(diff / 60_000);
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(diff / 3_600_000);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(diff / 86_400_000);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

interface IncidentDocumentsPanelProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  incident: Incident;
  // Uploads currently held in chat state. Treated as the authoritative
  // list while the drawer is open so the chat and drawer stay in sync
  // when uploads/removals happen from either surface.
  attachments: FileUpload[];
  // Saved snapshots (point-in-time PDFs) for this incident, fetched
  // by the page. The drawer renders them and routes user actions
  // back through these callbacks.
  snapshots: StorageUserFile[];
  // True while the initial snapshot fetch is in flight, so the
  // empty-state message doesn't flash before the data arrives.
  snapshotsLoading?: boolean;
  // The kind currently being saved as a snapshot (or null), so the
  // drawer can disable the trigger and show progress.
  savingSnapshotKind: string | null;
  onUpload: (file: File) => Promise<void>;
  onView: (fileId: string) => void;
  onRemove: (fileId: string) => Promise<void>;
  onToggleUploadScope: (fileId: string, newScope: "private" | "school") => Promise<void>;
  onSaveSnapshot: (kind: GeneratedArtifactKind) => Promise<void>;
  onViewSnapshot: (filePath: string) => void;
  onToggleSnapshotScope: (fileId: string, newScope: "private" | "school") => Promise<void>;
  onDeleteSnapshot: (fileId: string) => Promise<void>;
  // Opens a generated artifact's panel in the chat (or kicks off a
  // direct download). The drawer doesn't own these surfaces — it just
  // routes the click through the chat's imperative handle.
  onOpenArtifact: (kind: GeneratedArtifactKind) => void;
  isReadOnly?: boolean;
  // The signed-in user's id, so the drawer knows when an uploaded
  // file/snapshot belongs to someone else (read-only delete/toggle
  // for non-owners).
  currentUserId?: string | null;
  // True while the parent incident is still a draft (not yet persisted
  // to the DB). Mirrors the same flag the chat uses to gate uploads.
  isUnsavedDraft?: boolean;
}

// Static catalog of generated artifacts surfaced in the drawer. The
// "ready" predicate is recomputed against current incident state on
// every render, so an entry can move from locked → available the
// moment its prerequisites are satisfied.
type ArtifactDef = {
  kind: GeneratedArtifactKind;
  title: string;
  description: string;
  // `LucideIcon` (rather than a hand-rolled `ComponentType`) so the
  // `aria-hidden: Booleanish` shape from lucide's prop types is accepted
  // here without losing autocompletion.
  icon: LucideIcon;
  primaryActionLabel: string;
  primaryActionIcon: LucideIcon;
  // Returns either { ready: true } or { ready: false, lockedHint: string }.
  // We compute readiness inside the component so this list can stay
  // declarative.
  needs:
    | "always"
    | "stage:reflection"
    | "stage:conversation"
    | "stage:resolution"
    | "extended-stage";
  // True if this artifact supports being saved as a frozen PDF
  // snapshot. Currently only the incident packet wires this up; other
  // artifacts will follow in a later iteration.
  snapshotable?: boolean;
};

// Ordered by where each artifact lands in the workflow so the list
// reads as a timeline: Step 2 outputs first, then Step 3, then Step 4,
// then extended support.
const ARTIFACTS: ArtifactDef[] = [
  {
    kind: "student-reflection",
    title: "Student reflection form",
    description: "Grade-appropriate prompts for the student to fill in.",
    icon: BookOpen,
    primaryActionLabel: "Open",
    primaryActionIcon: ExternalLink,
    needs: "stage:reflection",
  },
  {
    kind: "conversation-guide",
    title: "Conversation guide",
    description: "Coaching prep for the restorative conversation.",
    icon: MessageCircle,
    primaryActionLabel: "Open",
    primaryActionIcon: ExternalLink,
    needs: "stage:reflection",
  },
  {
    kind: "parent-comm",
    title: "Parent communication",
    description: "Email, phone script, or call talking points.",
    icon: Mail,
    primaryActionLabel: "Open",
    primaryActionIcon: ExternalLink,
    needs: "always",
  },
  {
    kind: "case-record",
    title: "Case record (notes & narrative)",
    description:
      "The documentation sheet: what happened, Notes (incl. AI narrative), polish, and packet download.",
    icon: FileCheck2,
    primaryActionLabel: "Open",
    primaryActionIcon: ExternalLink,
    needs: "always",
  },
  {
    kind: "incident-packet",
    title: "Incident packet (PDF)",
    description: "One-page summary tying everything together.",
    icon: ClipboardList,
    primaryActionLabel: "Download",
    primaryActionIcon: Download,
    needs: "stage:resolution",
    snapshotable: true,
  },
  {
    kind: "iss-tasks",
    title: "ISS restorative tasks",
    description: "Work plan + reentry steps for in-school suspension.",
    icon: Briefcase,
    primaryActionLabel: "Open",
    primaryActionIcon: ExternalLink,
    needs: "extended-stage",
  },
];

// Persistent documents view for a single Behavior Incident. Surfaces
// uploaded files outside of chat scroll and provides a stable home for
// generated artifacts and saved snapshots (added in subsequent commits).
export const IncidentDocumentsPanel: React.FC<IncidentDocumentsPanelProps> = ({
  open,
  onOpenChange,
  incident,
  attachments,
  snapshots,
  snapshotsLoading = false,
  savingSnapshotKind,
  onUpload,
  onView,
  onRemove,
  onToggleUploadScope,
  onSaveSnapshot,
  onViewSnapshot,
  onToggleSnapshotScope,
  onDeleteSnapshot,
  onOpenArtifact,
  isReadOnly = false,
  currentUserId = null,
  isUnsavedDraft = false,
}) => {
  const [uploadingName, setUploadingName] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Reset transient state whenever the drawer closes so a re-open
  // shows a clean slate.
  useEffect(() => {
    if (!open) {
      setUploadingName(null);
      setRemovingId(null);
    }
  }, [open]);

  const triggerFilePicker = () => {
    if (isReadOnly || isUnsavedDraft) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setUploadingName(file.name);
    try {
      await onUpload(file);
    } catch (err) {
      const msg =
        err instanceof StorageServiceError
          ? err.message
          : "Upload failed. Please try again.";
      toast.error(msg);
    } finally {
      setUploadingName(null);
    }
  };

  const handleRemove = async (fileId: string) => {
    setRemovingId(fileId);
    try {
      await onRemove(fileId);
    } finally {
      setRemovingId(null);
    }
  };

  const studentLabel = incident.student_identifier?.trim() || "this incident";

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md md:max-w-lg p-0 flex flex-col"
      >
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-gray-200 bg-bodhi-teal text-white space-y-1">
          <div className="text-[10px] uppercase tracking-wider text-white/80 font-semibold">
            Behavior Incident Toolkit
          </div>
          <SheetTitle className="text-white font-literata text-xl">
            Documents
          </SheetTitle>
          <SheetDescription className="text-white/80 text-xs">
            Everything tied to {studentLabel}'s incident — uploads, generated
            forms, and saved snapshots.
          </SheetDescription>
        </SheetHeader>

        <TooltipProvider delayDuration={150}>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6 bg-white">
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileChange}
          />

          {/* GENERATED — wired in Commit B. Each row routes through the
              chat's imperative handle so opening an artifact uses the
              same code path the chat itself does. */}
          <GeneratedSection
            incident={incident}
            onOpenArtifact={onOpenArtifact}
            onSaveSnapshot={onSaveSnapshot}
            savingSnapshotKind={savingSnapshotKind}
          />

          {/* SAVED SNAPSHOTS — frozen PDFs the team can refer back to. */}
          <SnapshotsSection
            snapshots={snapshots}
            loading={snapshotsLoading}
            currentUserId={currentUserId}
            onView={onViewSnapshot}
            onToggleScope={onToggleSnapshotScope}
            onDelete={onDeleteSnapshot}
          />

          {/* UPLOADED FILES — fully wired in Commit A. */}
          <section>
            <SectionHeader
              title="Uploaded files"
              count={attachments.length}
              actions={
                !isReadOnly && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={triggerFilePicker}
                    disabled={!!uploadingName || isUnsavedDraft}
                  >
                    {uploadingName ? (
                      <>
                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        Uploading…
                      </>
                    ) : (
                      <>
                        <Upload className="w-3 h-3 mr-1" />
                        Upload
                      </>
                    )}
                  </Button>
                )
              }
            />

            {isUnsavedDraft && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mt-2">
                Add the student name and what happened first — then you can
                attach files.
              </p>
            )}

            {attachments.length === 0 && !isUnsavedDraft && (
              <p className="text-xs text-gray-500 italic px-1 mt-1">
                Nothing attached yet. Upload IEPs, witness statements, or
                screenshots so they're tied to this incident.
              </p>
            )}

            {attachments.length > 0 && (
              <ul className="mt-2 space-y-2">
                {attachments.map((a) => {
                  const isRemoving = removingId === a.id;
                  const isOwner = !!currentUserId && a.user_id === currentUserId;
                  return (
                    <li
                      key={a.id}
                      className={cn(
                        "flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2",
                        isRemoving && "opacity-60",
                      )}
                    >
                      <div className="w-8 h-8 rounded-md bg-bodhi-teal/10 text-bodhi-teal flex items-center justify-center flex-shrink-0">
                        <FileTypeIcon mimeType={a.mime_type} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {a.file_name}
                        </div>
                        <div className="text-[11px] text-gray-500 flex items-center gap-1.5 flex-wrap">
                          <span>{formatFileSize(a.file_size)}</span>
                          <span aria-hidden>·</span>
                          <span>{formatRelativeTime(a.created_at)}</span>
                          <span aria-hidden>·</span>
                          <span className={isOwner ? "text-gray-500" : "text-bodhi-teal/80"}>
                            {isOwner ? "by you" : "by a colleague"}
                          </span>
                          <ScopeBadge scope={a.share_scope} />
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => onView(a.id)}
                        className="text-bodhi-teal hover:bg-bodhi-teal/10 rounded-md px-2 py-1 text-xs font-medium flex items-center gap-1 transition-colors"
                        aria-label={`View ${a.file_name}`}
                      >
                        <Download className="w-3.5 h-3.5" aria-hidden />
                        View
                      </button>
                      {!isReadOnly && isOwner && (
                        <ScopeToggleButton
                          scope={a.share_scope === "school" ? "school" : "private"}
                          onToggle={(next) => onToggleUploadScope(a.id, next)}
                        />
                      )}
                      {!isReadOnly && isOwner && (
                        <button
                          type="button"
                          onClick={() => handleRemove(a.id)}
                          disabled={isRemoving}
                          className="text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md p-1.5 transition-colors disabled:opacity-50"
                          aria-label={`Remove ${a.file_name}`}
                        >
                          {isRemoving ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="w-3.5 h-3.5" aria-hidden />
                          )}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>
        </TooltipProvider>
      </SheetContent>
    </Sheet>
  );
};

// Renders the catalog of generated artifacts with stage-aware
// availability. Locked rows show a tooltip explaining what to finish
// next so leaders never click into a blank or invalid panel.
const GeneratedSection: React.FC<{
  incident: Incident;
  onOpenArtifact: (kind: GeneratedArtifactKind) => void;
  onSaveSnapshot: (kind: GeneratedArtifactKind) => Promise<void>;
  savingSnapshotKind: string | null;
}> = ({ incident, onOpenArtifact, onSaveSnapshot, savingSnapshotKind }) => {
  const completion = getStageCompletion(incident);
  const hasExtended = showsExtendedStage(incident);

  const visible = ARTIFACTS.filter((a) =>
    a.needs === "extended-stage" ? hasExtended : true,
  );

  const isReady = (a: ArtifactDef): boolean => {
    switch (a.needs) {
      case "always":
        return completion.incident; // need at least student name + description
      case "stage:reflection":
        return completion.reflection;
      case "stage:conversation":
        return completion.conversation;
      case "stage:resolution":
        return completion.conversation; // packet usable from resolution onward
      case "extended-stage":
        return hasExtended;
    }
  };

  const lockHint = (a: ArtifactDef): string => {
    switch (a.needs) {
      case "always":
        return "Add the student name and what happened first.";
      case "stage:reflection":
        return "Finish Step 2: Understand first.";
      case "stage:conversation":
        return "Finish Step 3: The Conversation first.";
      case "stage:resolution":
        return "Finish Step 3 to generate the packet.";
      case "extended-stage":
        return "Available when extended support is needed.";
    }
  };

  return (
    <section>
        <SectionHeader title="Generated" count={visible.length} />
        <ul className="space-y-2">
          {visible.map((a) => {
            const ready = isReady(a);
            const Icon = a.icon;
            const ActionIcon = a.primaryActionIcon;
            const button = (
              <button
                type="button"
                disabled={!ready}
                onClick={() => {
                  if (!ready) return;
                  onOpenArtifact(a.kind);
                }}
                className={cn(
                  "w-full flex items-center gap-3 rounded-lg border px-3 py-2.5 text-left transition-colors",
                  ready
                    ? "border-gray-200 bg-white hover:border-bodhi-teal/40 hover:bg-bodhi-teal/5"
                    : "border-gray-200 bg-gray-50 cursor-not-allowed",
                )}
              >
                <div
                  className={cn(
                    "w-8 h-8 rounded-md flex items-center justify-center flex-shrink-0",
                    ready
                      ? "bg-bodhi-teal/10 text-bodhi-teal"
                      : "bg-gray-200 text-gray-400",
                  )}
                >
                  {ready ? (
                    <Icon className="w-4 h-4" aria-hidden />
                  ) : (
                    <Lock className="w-3.5 h-3.5" aria-hidden />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div
                    className={cn(
                      "text-sm font-medium truncate",
                      ready ? "text-gray-900" : "text-gray-500",
                    )}
                  >
                    {a.title}
                  </div>
                  <div
                    className={cn(
                      "text-[11px] truncate",
                      ready ? "text-gray-500" : "text-gray-400",
                    )}
                  >
                    {a.description}
                  </div>
                </div>
                {ready && (
                  <span className="text-bodhi-teal text-xs font-medium flex items-center gap-1 flex-shrink-0">
                    <ActionIcon className="w-3.5 h-3.5" aria-hidden />
                    {a.primaryActionLabel}
                  </span>
                )}
              </button>
            );

            const saveBtn =
              ready && a.snapshotable ? (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        void onSaveSnapshot(a.kind);
                      }}
                      disabled={savingSnapshotKind === a.kind}
                      aria-label={`Save snapshot of ${a.title}`}
                      className="ml-2 text-gray-400 hover:text-bodhi-teal hover:bg-bodhi-teal/5 rounded-md p-1.5 transition-colors disabled:opacity-50 flex-shrink-0"
                    >
                      {savingSnapshotKind === a.kind ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Camera className="w-3.5 h-3.5" aria-hidden />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="left">
                    Save a dated snapshot to the school library
                  </TooltipContent>
                </Tooltip>
              ) : null;

            if (ready) {
              return (
                <li key={a.kind} className="flex items-stretch">
                  <div className="flex-1">{button}</div>
                  {saveBtn}
                </li>
              );
            }
            return (
              <li key={a.kind}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span tabIndex={0} className="block">
                      {button}
                    </span>
                  </TooltipTrigger>
                  <TooltipContent side="left">{lockHint(a)}</TooltipContent>
                </Tooltip>
              </li>
            );
          })}
        </ul>
      </section>
  );
};

// ── Snapshots section ────────────────────────────────────────────────
// Saved snapshots are point-in-time PDFs stored in the `file_uploads`
// table (consolidated from the legacy storage_user_files in AE-115).
// Owners can flip a snapshot between school-shared and private, or
// delete it; non-owners get view-only access.
const SnapshotsSection: React.FC<{
  snapshots: StorageUserFile[];
  loading: boolean;
  currentUserId: string | null;
  onView: (filePath: string) => void;
  onToggleScope: (fileId: string, newScope: "private" | "school") => Promise<void>;
  onDelete: (fileId: string) => Promise<void>;
}> = ({ snapshots, loading, currentUserId, onView, onToggleScope, onDelete }) => {
  const [busyId, setBusyId] = useState<string | null>(null);

  return (
    <section>
      <SectionHeader
        title="Saved snapshots"
        count={loading ? undefined : snapshots.length}
      />
      {loading ? (
        <p className="text-xs text-gray-400 italic px-1 flex items-center gap-1.5">
          <Loader2 className="w-3 h-3 animate-spin" />
          Loading snapshots…
        </p>
      ) : snapshots.length === 0 ? (
        <p className="text-xs text-gray-500 italic px-1">
          No saved snapshots yet. Tap the camera icon next to a generated
          artifact to save a dated copy here.
        </p>
      ) : (
        <ul className="space-y-2">
          {snapshots.map((s) => {
            const isOwner = !!currentUserId && s.user_id === currentUserId;
            const scope: "private" | "school" =
              s.share_scope === "school" ? "school" : "private";
            const isBusy = busyId === s.id;
            return (
              <li
                key={s.id}
                className={cn(
                  "flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2",
                  isBusy && "opacity-60",
                )}
              >
                <div className="w-8 h-8 rounded-md bg-bodhi-teal/10 text-bodhi-teal flex items-center justify-center flex-shrink-0">
                  <FileText className="w-4 h-4" aria-hidden />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">
                    {s.file_name}
                  </div>
                  <div className="text-[11px] text-gray-500 flex items-center gap-1.5 flex-wrap">
                    <span>{formatFileSize(s.file_size)}</span>
                    <span aria-hidden>·</span>
                    <span>{formatRelativeTime(s.created_at)}</span>
                    <span aria-hidden>·</span>
                    <span className={isOwner ? "text-gray-500" : "text-bodhi-teal/80"}>
                      {isOwner ? "by you" : "by a colleague"}
                    </span>
                    <ScopeBadge scope={scope} />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => onView(s.file_path)}
                  className="text-bodhi-teal hover:bg-bodhi-teal/10 rounded-md px-2 py-1 text-xs font-medium flex items-center gap-1 transition-colors"
                  aria-label={`View ${s.file_name}`}
                >
                  <Download className="w-3.5 h-3.5" aria-hidden />
                  View
                </button>
                {isOwner && (
                  <ScopeToggleButton
                    scope={scope}
                    onToggle={async (next) => {
                      setBusyId(s.id);
                      try {
                        await onToggleScope(s.id, next);
                      } finally {
                        setBusyId(null);
                      }
                    }}
                  />
                )}
                {isOwner && (
                  <button
                    type="button"
                    onClick={async () => {
                      setBusyId(s.id);
                      try {
                        await onDelete(s.id);
                      } finally {
                        setBusyId(null);
                      }
                    }}
                    disabled={isBusy}
                    className="text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-md p-1.5 transition-colors disabled:opacity-50"
                    aria-label={`Delete ${s.file_name}`}
                  >
                    {isBusy ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Trash2 className="w-3.5 h-3.5" aria-hidden />
                    )}
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

// ── Sharing primitives ───────────────────────────────────────────────
// Tiny visual badge so every row in the drawer surfaces who can see
// this file. Keeps the toggle reversible at a glance.
const ScopeBadge: React.FC<{ scope: "private" | "school" | "district" }> = ({
  scope,
}) => {
  if (scope === "school") {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] text-bodhi-teal/80">
        <Users className="w-2.5 h-2.5" aria-hidden />
        School
      </span>
    );
  }
  if (scope === "district") {
    return (
      <span className="inline-flex items-center gap-0.5 text-[10px] text-bodhi-teal/80">
        <Users className="w-2.5 h-2.5" aria-hidden />
        District
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-0.5 text-[10px] text-gray-500">
      <EyeOff className="w-2.5 h-2.5" aria-hidden />
      Private
    </span>
  );
};

// One-click toggle between private and school visibility for a row.
// Owners only — the parent gates rendering, so the button itself
// doesn't need to know.
const ScopeToggleButton: React.FC<{
  scope: "private" | "school";
  onToggle: (next: "private" | "school") => Promise<void>;
}> = ({ scope, onToggle }) => {
  const [busy, setBusy] = useState(false);
  const next: "private" | "school" = scope === "school" ? "private" : "school";
  const label =
    scope === "school" ? "Make private" : "Share with school";
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          onClick={async () => {
            setBusy(true);
            try {
              await onToggle(next);
            } finally {
              setBusy(false);
            }
          }}
          disabled={busy}
          className="text-gray-400 hover:text-bodhi-teal hover:bg-bodhi-teal/10 rounded-md p-1.5 transition-colors disabled:opacity-50"
          aria-label={label}
        >
          {busy ? (
            <Loader2 className="w-3.5 h-3.5 animate-spin" />
          ) : scope === "school" ? (
            <EyeOff className="w-3.5 h-3.5" aria-hidden />
          ) : (
            <Users className="w-3.5 h-3.5" aria-hidden />
          )}
        </button>
      </TooltipTrigger>
      <TooltipContent side="left">{label}</TooltipContent>
    </Tooltip>
  );
};

// Tally of items shown in this section's header. Kept as a tiny inline
// component so each section's title bar stays consistent.
const SectionHeader: React.FC<{
  title: string;
  count?: number;
  actions?: React.ReactNode;
}> = ({ title, count, actions }) => (
  <div className="flex items-center justify-between mb-2 px-1">
    <div className="flex items-baseline gap-2">
      <h3 className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">
        {title}
      </h3>
      {typeof count === "number" && (
        <span className="text-[10px] text-gray-400 font-medium">
          {count === 0 ? "" : count}
        </span>
      )}
    </div>
    {actions}
  </div>
);
